import Footer from "components/Footer/Footer";
import Navbar from "components/Navbar/Navbar";
import NexGenLogo from "components/NexGenLogo";
import MainLayout from "layout/MainLayout";
import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { mainRoutes } from "routes/routes";
import "./App.scss";

function App() {
  const location = useLocation();
  const pathname = location.pathname;

  const [menuActive, setMenuActive] = useState("home");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [delayAnimation, setDelayAnimation] = useState(false);

  /**
   * Initial loading
   */
  useEffect(() => {
    let frame;

    const started = performance.now();
    const duration = 1450;

    const tick = (now) => {
      const progress = Math.min((now - started) / duration, 1);

      setLoadingProgress(Math.round(progress * 100));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);

    const timer = window.setTimeout(() => {
      setLoading(false);
      setDelayAnimation(true);
      document.body.classList.remove("is-loading");
    }, duration + 120);

    document.body.classList.add("is-loading");

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      document.body.classList.remove("is-loading");
    };
  }, []);

  /**
   * Scroll position
   */
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;

      ticking = true;

      requestAnimationFrame(() => {
        setScrollPosition(window.scrollY || 0);
        ticking = false;
      });
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /**
   * Handle route change
   */
  useEffect(() => {
    // Luôn scroll top khi đổi route
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });

    // Jobs và Job detail
    if (pathname === "/jobs" || pathname.startsWith("/detail-job/")) {
      setMenuActive("jobs");
      return;
    }

    // Home
    if (pathname === "/") {
      const targetSection = location.state?.scrollTo;

      if (targetSection) {
        // Đợi Home render xong rồi mới tìm element
        requestAnimationFrame(() => {
          const element = document.getElementById(targetSection);

          if (element) {
            setMenuActive(targetSection);

            element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        });

        // Xóa state để refresh không scroll lại
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );

        return;
      }

      setMenuActive("home");
      return;
    }

    setMenuActive("");
  }, [pathname, location.state]);

  /**
   * Select menu
   */
  const handleSelectMenu = useCallback(
    (menuSelect) => {
      setMenuActive(menuSelect);

      // Không phải Home
      if (pathname !== "/") {
        return;
      }

      const element = document.getElementById(menuSelect);

      if (!element) return;

      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    },
    [pathname],
  );

  return (
    <div className="App">
      {loading && (
        <div className="page-loader" aria-label="Loading NexGen Technology">
          <div className="loader-inner">
            <NexGenLogo dark />

            <div className="loader-meta">
              <span>Technology Solutions</span>
              <span>{loadingProgress}%</span>
            </div>

            <div className="loader-track">
              <span
                style={{
                  width: `${loadingProgress}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="landing-container">
        <Navbar
          onSelectMenu={handleSelectMenu}
          menuActive={menuActive}
          scrollPosition={scrollPosition}
        />

        <main className="landing-body">
          <Routes>
            {mainRoutes.map((route) => {
              const Page = route.component;

              return (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <MainLayout>
                      <Page
                        menuSelect={menuActive}
                        scrollPosition={scrollPosition}
                        onSelectMenu={handleSelectMenu}
                        delayAnimation={delayAnimation}
                      />
                    </MainLayout>
                  }
                />
              );
            })}

            <Route path="*" element={<Navigate to="/not-found" replace />} />
          </Routes>

          <Footer />
        </main>

        <ToastContainer
          position="top-right"
          closeOnClick
          pauseOnFocusLoss
          autoClose={5000}
          hideProgressBar={false}
          limit={3}
          draggable
          style={{ width: "320px" }}
        />
      </div>
    </div>
  );
}

export default App;
